/**
 * Admin-domain endpoint constants for business resources (users, memberships,
 * hardware, reclaims, fraud, disputes, audit).
 *
 * Note: auth/session/health endpoints live in `endpoints.ts` under
 * ADMIN_ENDPOINTS. This file covers everything reachable from feature pages.
 *
 * Each read/mutation has two representations:
 *   - A callable builder (or string for paramless endpoints) under
 *     ADMIN_READ_ENDPOINTS / ADMIN_MUTATION_ENDPOINTS — used by api clients.
 *   - A pattern string under ADMIN_READ_ENDPOINT_PATTERNS /
 *     ADMIN_MUTATION_ENDPOINT_PATTERNS — used for contract stability tests
 *     and OpenAPI cross-referencing.
 */

export const ADMIN_READ_ENDPOINTS = {
  operationsSummary: "/admin/operations/summary",
  membershipFunnel: "/admin/operations/membership-funnel",
  navigationMetrics: "/admin/navigation/metrics",
  searchUsers: "/admin/users/search",
  userDetail: (userId: string) => `/admin/users/${userId}`,
  userTimeline: (userId: string) => `/admin/users/${userId}/timeline`,
  userAuditEvents: (userId: string) => `/admin/users/${userId}/audit-events`,
  userSessions: (userId: string) => `/admin/users/${userId}/sessions`,
  userMembership: (userId: string) => `/admin/users/${userId}/membership`,
  userGifts: (userId: string) => `/admin/users/${userId}/gifts`,
  // Finance: remaining gifted membership across the whole book. Company-wide
  // aggregate, gated on its own `finance_reports:view` permission rather than
  // membership:view — reading total company liability should not ride on the
  // ability to open one member's record.
  // WS-F. A GET, unlike the gift SEARCH: nothing here takes a gift code, so
  // there is nothing that must be kept out of a URL.
  giftRiskReviews: "/admin/gifts/risk-reviews",
  giftObligations: "/admin/reports/gift-obligations",
  userBillingRetry: (userId: string) =>
    `/admin/users/${userId}/billing-retry`,
  // W65-3: the member's payments, read live from Stripe (membership:view).
  userPayments: (userId: string) => `/admin/users/${userId}/payments`,
  membershipNoticesSummary: "/admin/membership-notices/summary",
  membershipNoticesFailed: "/admin/membership-notices/failed",
  membershipNoticesDuePending: "/admin/membership-notices/due-pending",
  listShipments: "/admin/shipments",
  outboxHealth: "/admin/reclaims/outbox-health",
  easypostWebhookEvents: "/admin/reclaims/easypost-webhook-events",
  easypostWebhookHealth: "/admin/reclaims/easypost-webhook-events/health",
  userHardware: (userId: string) => `/admin/users/${userId}/hardware`,
  userReclaim: (userId: string) => `/admin/users/${userId}/reclaim`,
  userLostReports: (userId: string) =>
    `/admin/users/${userId}/hardware-lost-reports`,
  // ONE global price, not per-user. Read needs hardware:view only.
  hardwareReplacementPrice: "/admin/hardware/replacement-price",
  // WF-47 dimension 5. Always all three platforms (ios/android/global), nulls
  // when unset. Read needs app_version:view, granted to all four roles
  // (owner, ops_admin, support, viewer) — only app_version:set is owner-only.
  adminAppVersion: "/admin/app-version",
  adminPolicyVersions: "/admin/policy-versions",
  userFraudSignals: (userId: string) =>
    `/admin/fraud/users/${userId}/signals`,
  listMemberships: "/admin/memberships",
  listHardware: "/admin/hardware",
  listReclaims: "/admin/reclaims",
  listDisputes: "/admin/disputes",
  disputeDetail: (disputeId: string) => `/admin/disputes/${disputeId}`,
  disputeEvidence: (disputeId: string) =>
    `/admin/disputes/${disputeId}/evidence`,
  auditLogs: "/admin/audit-logs",
  auditEvent: (eventId: string) => `/admin/audit-logs/${eventId}`,
  supportActionPlaceholders: "/admin/support-actions/placeholders",
  supportActionQueue: "/admin/support-actions/queue",
} as const;

export const ADMIN_MUTATION_ENDPOINTS = {
  forcePasswordReset: (userId: string) =>
    `/admin/users/${userId}/force-password-reset`,
  forceSignout: (userId: string) => `/admin/users/${userId}/force-signout`,
  lockAccount: (userId: string) => `/admin/users/${userId}/lock`,
  unlockAccount: (userId: string) => `/admin/users/${userId}/unlock`,
  dataExport: (userId: string) => `/admin/users/${userId}/data-export`,
  accountDeletion: (userId: string) =>
    `/admin/users/${userId}/account-deletion`,
  // WF-56 — one call runs the whole minor-discovered cascade (suspend, lock,
  // revoke sessions, queue the window-exempt mandatory refund, typed audit).
  agePolicySuspend: (userId: string) =>
    `/admin/users/${userId}/age-policy-suspend`,
  // Keyed by membership_id (NOT user_id) — mirrors the gateway's admin
  // membership_overrides router (`/admin/memberships/{membership_id}/...`).
  // This one WAS user-keyed while it was an unwired placeholder; WF-66 shipped
  // the real route per-membership, like its four siblings.
  overrideMembershipState: (membershipId: string) =>
    `/admin/memberships/${membershipId}/override-state`,
  extendTrial: (membershipId: string) =>
    `/admin/memberships/${membershipId}/trial-extend`,
  overrideCommitment: (membershipId: string) =>
    `/admin/memberships/${membershipId}/commitment-override`,
  convertTrial: (userId: string) =>
    `/admin/users/${userId}/membership/convert-trial`,
  rebindHardware: (userId: string) =>
    `/admin/users/${userId}/hardware/rebind`,
  loadSerialInventory: "/admin/fulfillment/inventory/serials",
  markHardwareLost: (serial: string) =>
    `/admin/hardware/${serial}/mark-lost`,
  blacklistHardware: (serial: string) =>
    `/admin/hardware/${serial}/blacklist`,
  approveShipmentReplacement: (shipmentId: string) =>
    `/admin/shipments/${shipmentId}/approve-replacement`,
  fileCarrierClaim: (shipmentId: string) =>
    `/admin/shipments/${shipmentId}/file-claim`,
  // PUT (not POST) — the append-only price write. Owner-only server-side.
  setHardwareReplacementPrice: "/admin/hardware/replacement-price",
  // WF-47 dimension 5. PUT (append-only floor write). Owner-only server-side
  // (app_version:set). A null minVersion clears the gate for that platform.
  setAppVersionFloor: "/admin/app-version",
  setPolicyVersion: "/admin/policy-versions",
  // Resolve a suspended trial's lost-hardware report. `userId` is a REQUIRED
  // scope, not decoration: the backend loads the report from a global table and
  // charges its owner, so an unscoped call would charge whoever owns reportId.
  resolveLostReport: (userId: string, reportId: string) =>
    `/admin/users/${userId}/hardware-lost-reports/${reportId}/resolve`,
  // WS-7 (Auth Brief §14 line 317, "required before launch"). Search is a POST
  // because one of the five lookup keys IS a gift code: a GET would put it in
  // access logs, browser history and the Referer header, which line 324 forbids.
  giftSearch: "/admin/gifts/search",
  giftRedemptionLink: (orderNumber: string) =>
    `/admin/gifts/${orderNumber}/redemption-link`,
  giftResend: (orderNumber: string) => `/admin/gifts/${orderNumber}/resend`,
  giftRevoke: (orderNumber: string) => `/admin/gifts/${orderNumber}/revoke`,
  giftRegenerate: (orderNumber: string) =>
    `/admin/gifts/${orderNumber}/regenerate`,
  // WS-6b-1. Refunds the buyer's original payment method and ends the gift. The
  // server re-decides eligibility, so this is safe to call from a stale row.
  giftRefund: (orderNumber: string) => `/admin/gifts/${orderNumber}/refund`,
  // WS-F. Records a CONCLUSION about a flagged redemption — it changes nothing
  // about the gift, the membership or the hardware.
  giftRiskResolve: (reviewId: number) =>
    `/admin/gifts/risk-reviews/${reviewId}/resolve`,
  issueReclaimLabel: (rmaId: string) =>
    `/admin/reclaims/${rmaId}/issue-label`,
  markReclaimReceived: (rmaId: string) =>
    `/admin/reclaims/${rmaId}/mark-received`,
  refurbQc: (rmaId: string) => `/admin/reclaims/${rmaId}/refurb-qc`,
  restockingFee: (rmaId: string) => `/admin/reclaims/${rmaId}/restocking-fee`,
  // WF-76 damage fee. The ORDER these appear in is the order they must happen:
  // evidence, then the notice that starts the five-business-day window, then a
  // contest if one arrives. The charge (restockingFee, above) refuses without them.
  qcEvidence: (rmaId: string) => `/admin/reclaims/${rmaId}/qc-evidence`,
  damageFeeNotice: (rmaId: string) => `/admin/reclaims/${rmaId}/damage-fee-notice`,
  damageFeeContest: (rmaId: string) => `/admin/reclaims/${rmaId}/damage-fee-contest`,
  overrideTrialBlock: (userId: string) =>
    `/admin/fraud/users/${userId}/override-trial-block`,
  householdException: (userId: string) =>
    `/admin/fraud/users/${userId}/household-exception`,
  submitDisputeEvidence: (disputeId: string) =>
    `/admin/disputes/${disputeId}/submit-evidence`,
  resolveDispute: (disputeId: string) =>
    `/admin/disputes/${disputeId}/resolve`,
  // Membership-ops mutations (suspend / restore / risk-decision / notice requeue).
  suspendMembership: (membershipId: string) =>
    `/admin/memberships/${membershipId}/suspend`,
  restoreMembership: (membershipId: string) =>
    `/admin/memberships/${membershipId}/restore`,
  createMembershipRiskDecision: "/admin/membership-risk-decisions",
  applyMembershipRiskDecision: (decisionId: string) =>
    `/admin/membership-risk-decisions/${decisionId}/apply`,
  requeueMembershipNotice: (noticeId: string) =>
    `/admin/membership-notices/${noticeId}/requeue`,
  // Staging-only demo controls (L71/L72/L73I).
  simulateFailedRenewal: "/admin/testing/billing/simulate-failed-renewal",
  cleanupSyntheticUser: "/admin/testing/demo/cleanup-synthetic-user",
} as const;

export const ADMIN_READ_ENDPOINT_PATTERNS = [
  "/admin/operations/summary",
  "/admin/operations/membership-funnel",
  "/admin/users/search",
  "/admin/users/{user_id}",
  "/admin/users/{user_id}/timeline",
  "/admin/users/{user_id}/audit-events",
  "/admin/users/{user_id}/sessions",
  "/admin/users/{user_id}/membership",
  "/admin/users/{user_id}/gifts",
  "/admin/reports/gift-obligations",
  "/admin/users/{user_id}/billing-retry",
  "/admin/users/{user_id}/payments",
  "/admin/membership-notices/summary",
  "/admin/users/{user_id}/hardware",
  "/admin/users/{user_id}/reclaim",
  "/admin/users/{user_id}/hardware-lost-reports",
  "/admin/hardware/replacement-price",
  "/admin/app-version",
  "/admin/policy-versions",
  "/admin/fraud/users/{user_id}/signals",
  "/admin/hardware",
  "/admin/reclaims",
  "/admin/disputes",
  "/admin/disputes/{dispute_id}",
  "/admin/disputes/{dispute_id}/evidence",
  "/admin/audit-logs",
  "/admin/audit-logs/{event_id}",
  "/admin/support-actions/placeholders",
  "/admin/support-actions/queue",
] as const;

export const ADMIN_MUTATION_ENDPOINT_PATTERNS = [
  "/admin/users/{user_id}/force-password-reset",
  "/admin/users/{user_id}/force-signout",
  "/admin/users/{user_id}/lock",
  "/admin/users/{user_id}/unlock",
  "/admin/users/{user_id}/data-export",
  "/admin/users/{user_id}/account-deletion",
  "/admin/users/{user_id}/age-policy-suspend",
  "/admin/users/{user_id}/membership/override-state",
  "/admin/memberships/{membership_id}/trial-extend",
  "/admin/memberships/{membership_id}/commitment-override",
  "/admin/users/{user_id}/membership/convert-trial",
  "/admin/users/{user_id}/hardware/rebind",
  "/admin/fulfillment/inventory/serials",
  "/admin/hardware/{serial}/mark-lost",
  "/admin/hardware/{serial}/blacklist",
  "/admin/hardware/replacement-price",
  "/admin/app-version",
  "/admin/policy-versions",
  "/admin/users/{user_id}/hardware-lost-reports/{report_id}/resolve",
  "/admin/reclaims/{rma_id}/issue-label",
  "/admin/reclaims/{rma_id}/mark-received",
  "/admin/reclaims/{rma_id}/refurb-qc",
  "/admin/reclaims/{rma_id}/restocking-fee",
  "/admin/reclaims/{rma_id}/qc-evidence",
  "/admin/reclaims/{rma_id}/damage-fee-notice",
  "/admin/reclaims/{rma_id}/damage-fee-contest",
  "/admin/fraud/users/{user_id}/override-trial-block",
  "/admin/fraud/users/{user_id}/household-exception",
  "/admin/disputes/{dispute_id}/submit-evidence",
  "/admin/disputes/{dispute_id}/resolve",
  "/admin/memberships/{membership_id}/suspend",
  "/admin/memberships/{membership_id}/restore",
  "/admin/membership-risk-decisions",
  "/admin/membership-risk-decisions/{decision_id}/apply",
  "/admin/membership-notices/{notice_id}/requeue",
  "/admin/testing/billing/simulate-failed-renewal",
  "/admin/testing/demo/cleanup-synthetic-user",
] as const;

export type AdminReadEndpointPattern =
  (typeof ADMIN_READ_ENDPOINT_PATTERNS)[number];
export type AdminMutationEndpointPattern =
  (typeof ADMIN_MUTATION_ENDPOINT_PATTERNS)[number];
